"use client";

import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { withContactDetails } from "@/data/knowledge/company";
import {
  useTranslation,
  type Locale,
  type TranslationKey,
} from "@/lib/i18n";
import { resolvePath, resolveRoute } from "@/lib/chat/routes";
import type { ChatAction, ContactSubject } from "@/lib/chat/types";

/**
 * Something the assistant offered, rendered as a control the visitor operates.
 *
 * Nothing here happens on its own. A contact enquiry is sent by the visitor
 * submitting this form, and a destination is an ordinary `<Link>` they choose
 * — never a `router.push` driven by the model. That is the whole point of the
 * arrangement: `resolvePath` turns a closed registry key into a path, so no
 * string the model produced is ever treated as a URL.
 */

const SUBJECTS: readonly ContactSubject[] = ["ai", "software", "infra", "other"];

function Destination({
  href,
  title,
  cta,
  label,
  compact,
}: {
  href: string;
  title: string;
  cta: string;
  /** The link's accessible name: the CTA joined with the page it leads to. */
  label: string;
  compact: boolean;
}) {
  return (
    <div
      className={`rounded-lg border border-assistant-divider bg-assistant-field ${
        compact ? "p-3" : "p-3.5"
      }`}
    >
      <div className="mb-2 text-xs font-bold text-text-primary">{title}</div>
      {/*
        Named in full because link lists read names, not the title above it:
        "Seite öffnen" alone is the same name for every destination.
      */}
      <Link
        href={href}
        aria-label={label}
        className="inline-flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-widest text-mint-text transition-colors hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint-text"
      >
        {cta}
        <ArrowRight size={12} aria-hidden="true" />
      </Link>
    </div>
  );
}

function ContactHandoff({
  action,
  compact,
}: {
  action: Extract<ChatAction, { kind: "contact" }>;
  compact: boolean;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");
  const [dismissed, setDismissed] = useState(false);
  // Per instance: a conversation can hold more than one of these forms, and
  // fixed ids would point every label at the first one.
  const id = useId();
  const titleId = `${id}-title`;

  /*
   * What replaces the form once it is sent or dismissed. When the form held
   * focus, removing it drops focus on <body>, outside the conversation; it is
   * taken back here instead. Only from <body>, though — a visitor who moved on
   * to the composer while the enquiry was sending keeps their place.
   */
  const doneRef = useRef<HTMLParagraphElement>(null);
  const done = dismissed || status === "success";
  useEffect(() => {
    if (!done) return;
    const active = document.activeElement;
    if (!active || active === document.body) doneRef.current?.focus();
  }, [done]);

  // Not `null`: an empty placeholder is what focus can land on after
  // dismissing, and what tells a screen reader the offer is gone.
  if (dismissed) {
    return (
      <p ref={doneRef} tabIndex={-1} className="sr-only">
        {t("chat.action.dismissed")}
      </p>
    );
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");

    const formData = new FormData(e.currentTarget);
    // The same body `src/components/Contact.tsx` sends to the same endpoint.
    const data = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      subject: formData.get("subject") as string,
      message: formData.get("message") as string,
    };

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      setStatus(res.ok ? "success" : "error");
    } catch {
      // Reported in the conversation, never as a page error.
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <p
        ref={doneRef}
        tabIndex={-1}
        className="rounded-lg border border-mint-text/25 bg-mint/12 p-3 text-xs text-text-primary"
        role="status"
      >
        {t("chat.action.contact.success")}
      </p>
    );
  }

  const field =
    "w-full rounded-sm border border-assistant-divider bg-assistant-surface px-2.5 py-1.5 text-xs text-text-primary placeholder:text-text-muted focus:border-mint-text focus:outline-none";

  return (
    <form
      onSubmit={handleSubmit}
      aria-labelledby={titleId}
      className={`flex flex-col gap-2 rounded-lg border border-assistant-divider bg-assistant-field ${
        compact ? "p-3" : "p-3.5"
      }`}
    >
      <div id={titleId} className="text-xs font-bold text-text-primary">
        {t("chat.action.contact.title")}
      </div>
      <p className="text-[11px] leading-relaxed text-text-muted">
        {t("chat.action.contact.hint")}
      </p>

      <label className="sr-only" htmlFor={`${id}-name`}>
        {t("contact.form.name")}
      </label>
      <input
        id={`${id}-name`}
        name="name"
        required
        defaultValue={action.name ?? ""}
        placeholder={t("contact.form.name")}
        className={field}
      />

      <label className="sr-only" htmlFor={`${id}-email`}>
        {t("contact.form.email")}
      </label>
      <input
        id={`${id}-email`}
        name="email"
        type="email"
        required
        defaultValue={action.email ?? ""}
        placeholder={t("contact.form.email")}
        className={field}
      />

      <label className="sr-only" htmlFor={`${id}-subject`}>
        {t("contact.form.subject")}
      </label>
      <select
        id={`${id}-subject`}
        name="subject"
        defaultValue={action.subject}
        className={field}
      >
        {SUBJECTS.map((s) => (
          <option key={s} value={s}>
            {t(`contact.form.subject.${s}` as "contact.form.subject.ai")}
          </option>
        ))}
      </select>

      <label className="sr-only" htmlFor={`${id}-message`}>
        {t("contact.form.message")}
      </label>
      <textarea
        id={`${id}-message`}
        name="message"
        required
        rows={3}
        defaultValue={action.message ?? ""}
        placeholder={t("contact.form.message")}
        className={`${field} resize-none`}
      />

      {status === "error" && (
        <p className="text-[11px] text-red-700 dark:text-red-400" role="alert">
          {withContactDetails(t("chat.action.contact.error"))}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={status === "sending"}
          className="rounded-sm bg-mint px-3 py-1.5 text-[10px] font-mono font-bold uppercase tracking-widest text-bg-primary transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {status === "sending"
            ? t("chat.action.contact.sending")
            : t("chat.action.contact.submit")}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-[10px] font-mono uppercase tracking-widest text-text-muted transition-colors hover:text-text-secondary"
        >
          {t("chat.action.dismiss")}
        </button>
      </div>
    </form>
  );
}

export default function ActionHandoff({
  action,
  compact,
}: {
  action: ChatAction;
  compact: boolean;
}) {
  const { t, locale } = useTranslation();

  if (action.kind === "contact") {
    return <ContactHandoff action={action} compact={compact} />;
  }

  if (action.kind === "readiness") {
    const href = resolvePath("ai-readiness");
    if (!href) return null;
    return (
      <Destination
        href={href}
        title={t("chat.action.readiness.title")}
        cta={t("chat.action.readiness.cta")}
        label={t("chat.action.readiness.label").replace(
          "{page}",
          t("chat.action.readiness.title"),
        )}
        compact={compact}
      />
    );
  }

  // A destination the model named by key. An unknown key resolves to nothing
  // and renders nothing, rather than becoming a link to somewhere arbitrary.
  const entry = resolveRoute(action.route);
  if (!entry || !entry.offerable) return null;
  return (
    <Destination
      href={entry.path}
      title={entry.label[locale]}
      cta={t("chat.action.navigate.cta")}
      label={t("chat.action.navigate.label").replace(
        "{page}",
        entry.label[locale],
      )}
      compact={compact}
    />
  );
}

/**
 * What the conversation's live region says when an action arrives, so a
 * screen-reader user learns a control appeared without focus being moved to
 * it. `null` exactly where ActionHandoff renders nothing.
 */
export function actionAnnouncement(
  action: ChatAction,
  t: (key: TranslationKey) => string,
  locale: Locale,
): string | null {
  if (action.kind === "contact") return t("chat.action.contact.announce");
  if (action.kind === "readiness") {
    if (!resolvePath("ai-readiness")) return null;
    return t("chat.action.navigate.announce").replace(
      "{page}",
      t("chat.action.readiness.title"),
    );
  }
  const entry = resolveRoute(action.route);
  if (!entry || !entry.offerable) return null;
  return t("chat.action.navigate.announce").replace(
    "{page}",
    entry.label[locale],
  );
}
